import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import './InvitationAcceptPage.css';

import { Navbar } from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { errorMessage } from '../services/apiError';
import { organizationService, OrganizationInvitationDetails } from '../services/organizationService';

const roleLabels: Record<string, string> = { admin: 'Admin', manager: 'Manager', member: 'Member' };

interface OrgInviteState {
  invitation: OrganizationInvitationDetails | null;
  loading: boolean;
  error: string | null;
}

const LoadingView: React.FC = () => (
  <div className="invitation-page">
    <div className="invitation-card">
      <div className="invitation-loading">Loading invitation...</div>
    </div>
  </div>
);

const ErrorView: React.FC<{ error: string; onGoHome: () => void }> = ({ error, onGoHome }) => (
  <div className="invitation-page">
    <div className="invitation-card">
      <div className="invitation-error">
        <div className="invitation-error__icon">!</div>
        <h2>Invalid Invitation</h2>
        <p>{error}</p>
        <button className="btn-primary" onClick={onGoHome}>Go Home</button>
      </div>
    </div>
  </div>
);

const InvitationHeader: React.FC = () => (
  <div className="invitation-header">
    <div className="invitation-icon">+</div>
    <h1>Organization Invitation</h1>
  </div>
);

const InvitationContent: React.FC<{
  invitation: OrganizationInvitationDetails;
  error: string | null;
}> = ({ invitation, error }) => (
  <div className="invitation-content">
    <p className="invitation-message">
      <strong>{invitation.inviter_name}</strong> has invited you to join
    </p>
    <div className="invitation-project">
      <span className="invitation-project__name">{invitation.organization_name}</span>
      <span className="invitation-project__role">as {roleLabels[invitation.role] || invitation.role}</span>
    </div>
    {invitation.is_expired && <div className="invitation-expired">This invitation has expired</div>}
    {error && <div className="invitation-error-inline">{error}</div>}
  </div>
);

const LoginPrompt: React.FC<{ email: string; token: string | undefined; onNavigate: (path: string) => void }> = ({
  email,
  token,
  onNavigate,
}) => (
  <div className="invitation-login-prompt">
    <p>Please sign in or register with <strong>{email}</strong> to accept this invitation.</p>
    <button className="btn-primary" onClick={() => onNavigate(`/?redirect=/organization-invite/${token}`)}>
      Sign In
    </button>
  </div>
);

const InvitationActions: React.FC<{
  accepting: boolean;
  isAuthenticated: boolean;
  isEmailMismatch: boolean;
  invitation: OrganizationInvitationDetails;
  token: string | undefined;
  onAccept: () => void;
  onNavigate: (path: string) => void;
}> = ({ accepting, isAuthenticated, isEmailMismatch, invitation, token, onAccept, onNavigate }) => {
  if (!isAuthenticated) {
    return (
      <div className="invitation-actions invitation-actions--single">
        <LoginPrompt email={invitation.email} token={token} onNavigate={onNavigate} />
      </div>
    );
  }

  if (invitation.is_expired) {
    return (
      <div className="invitation-actions invitation-actions--single">
        <button className="btn-secondary" onClick={() => onNavigate('/')}>Go Home</button>
      </div>
    );
  }

  return (
    <div className="invitation-actions">
      <button className="btn-secondary" onClick={() => onNavigate('/')} disabled={accepting}>Decline</button>
      <button className="btn-primary" onClick={onAccept} disabled={accepting || isEmailMismatch}>
        {accepting ? 'Accepting...' : 'Accept Invitation'}
      </button>
    </div>
  );
};

const EmailMismatchWarning: React.FC<{
  userEmail?: string;
  invitationEmail: string;
  isVisible: boolean;
}> = ({ userEmail, invitationEmail, isVisible }) => {
  if (!isVisible) return null;

  return (
    <div className="invitation-email-mismatch">
      This invitation was sent to <strong>{invitationEmail}</strong>. You are signed in as{' '}
      <strong>{userEmail}</strong>.
    </div>
  );
};

const useOrgInvitation = (token: string | undefined): OrgInviteState & {
  setError: (error: string | null) => void;
} => {
  const [state, setState] = useState<OrgInviteState>({
    invitation: null,
    loading: Boolean(token),
    error: token ? null : 'Invalid invitation link',
  });

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    organizationService.getInvitation(token)
      .then((invitation) => !cancelled && setState({ invitation, loading: false, error: null }))
      .catch(() => !cancelled && setState({ invitation: null, loading: false, error: 'Invitation not found or has expired' }));

    return () => { cancelled = true; };
  }, [token]);

  return { ...state, setError: (error) => setState((current) => ({ ...current, error })) };
};

const useAcceptInvitation = (
  token: string | undefined,
  isAuthenticated: boolean,
  onAccepted: () => void,
  setError: (error: string | null) => void,
) => {
  const [accepting, setAccepting] = useState(false);
  // eslint-disable-next-line max-statements
  const accept = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    setAccepting(true);
    setError(null);
    try {
      await organizationService.acceptInvitation(token);
      onAccepted();
    } catch (err) {
      setError(errorMessage(err, 'We couldn’t accept this invitation. Check that it is still valid and try again.'));
      setAccepting(false);
    }
  }, [isAuthenticated, onAccepted, setError, token]);

  return { accepting, accept };
};

const OrganizationInvitationCard: React.FC<{
  accepting: boolean;
  auth: ReturnType<typeof useAuth>;
  error: string | null;
  invitation: OrganizationInvitationDetails;
  token: string | undefined;
  onAccept: () => void;
  onNavigate: (path: string) => void;
}> = ({ accepting, auth, error, invitation, token, onAccept, onNavigate }) => {
  const isEmailMismatch = Boolean(auth.isAuthenticated && auth.user?.email !== invitation.email);

  return (
    <div className="invitation-card">
      <InvitationHeader />
      <InvitationContent invitation={invitation} error={error} />
      <InvitationActions
        accepting={accepting}
        isAuthenticated={auth.isAuthenticated}
        isEmailMismatch={isEmailMismatch}
        invitation={invitation}
        token={token}
        onAccept={onAccept}
        onNavigate={onNavigate}
      />
      <EmailMismatchWarning
        userEmail={auth.user?.email}
        invitationEmail={invitation.email}
        isVisible={isEmailMismatch}
      />
    </div>
  );
};

// eslint-disable-next-line max-statements
export const OrganizationInvitationAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const state = useOrgInvitation(token);
  const accepted = useCallback(() => navigate('/organizations'), [navigate]);
  const { accepting, accept } = useAcceptInvitation(token, auth.isAuthenticated, accepted, state.setError);

  if (state.loading) return <LoadingView />;
  if (state.error && !state.invitation) return <ErrorView error={state.error} onGoHome={() => navigate('/')} />;
  if (!state.invitation) return null;

  return (
    <>
      <Navbar />
      <div className="invitation-page invitation-page--with-navbar">
        <OrganizationInvitationCard
          accepting={accepting}
          auth={auth}
          error={state.error}
          invitation={state.invitation}
          token={token}
          onAccept={accept}
          onNavigate={navigate}
        />
      </div>
    </>
  );
};
